import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { buildApiCallEvent } from './api-call-telemetry';
import { logApiCall } from './perf-telemetry';

// Conservative bound to stop unbounded socket-retry loops from masking slow upstreams.
// Tighten once FE-1 telemetry confirms p99 latency.
const REQUEST_TIMEOUT_MS = 60000;

const getRestBaseEndpoint = () => {
  //getDeployOptions() doesnt seem to be working
  const options = (window as any).__DEPLOY_OPTIONS;
  return options.restApiUrl;
};

const axiosInstance: AxiosInstance = axios.create({
  baseURL: getRestBaseEndpoint(),
  timeout: REQUEST_TIMEOUT_MS,
});

const attachTokenToRequest = (config: AxiosRequestConfig) => {
  let localStorageVal = localStorage.getItem('quipa-store');
  const parsedVal = localStorageVal ? JSON.parse(localStorageVal) : null;

  if (parsedVal && parsedVal.user) {
    // NOSONAR
    //current typescript version doesnt support optional chaining, hence NOSONAR above
    const { token } = parsedVal.user;
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

const stampStartTime = (config: AxiosRequestConfig) => {
  (config as any).__perfStart =
    typeof performance !== 'undefined' ? performance.now() : Date.now();
  return config;
};

const elapsedMs = (config: AxiosRequestConfig | undefined): number => {
  if (!config) return 0;
  const start = (config as any).__perfStart;
  if (typeof start !== 'number') return 0;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return now - start;
};

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    logApiCall(
      buildApiCallEvent({
        config: response.config,
        response,
        elapsedMs: elapsedMs(response.config),
      })
    );
    return response;
  },
  async (error) => {
    console.log('error : ', error, error.response);
    const originalRequest = error.config;
    if (
      error.response &&
      error.response.data &&
      error.response.data.message &&
      error.response.data.message.message === 'jwt expired' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      let store: any = localStorage.getItem('quipa-store');
      let refreshToken;
      if (store !== null) {
        store = JSON.parse(store);
        refreshToken = store.user.refreshToken;
      }
      if (refreshToken) {
        const { data } = await axiosInstance.post('/refresh-token', {
          refreshToken,
          accessTokenExpiry: '5m',
        });
        localStorage.setItem(
          'quipa-store',
          JSON.stringify({ ...store, user: { ...store.user, token: data.accessToken } })
        );

        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;
        return axiosInstance(originalRequest);
      }
    }

    let msg = '';
    if (error && error.response && error.response.data) {
      const m = error.response.data.message;
      if (typeof m === 'string') msg = m;
      else if (m && typeof m.message === 'string') msg = m.message;
    }
    if (!msg && error && error.message) msg = String(error.message);

    const looksLikeStaleSocket =
      Boolean(msg) && msg.includes('This socket has been ended by the other party');

    if (looksLikeStaleSocket && !originalRequest._retrySocket) {
      originalRequest._retrySocket = true;
      const delay = 200 * (0.5 + Math.random());
      await new Promise((r) => setTimeout(r, delay));
      return axiosInstance(originalRequest);
    }

    logApiCall(
      buildApiCallEvent({
        config: originalRequest,
        response: error.response,
        error,
        elapsedMs: elapsedMs(originalRequest),
      })
    );

    return Promise.reject(error);
  }
);

axiosInstance.interceptors.request.use(attachTokenToRequest);
axiosInstance.interceptors.request.use(stampStartTime);

export default axiosInstance;
